import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, Upload, CheckCircle2, Loader2, FileText, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";
import { BUSINESS } from "@/lib/constants";
import { toast } from "sonner";

export default function Consultation() {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", ticket_location: "", message: "" });
  const [file, setFile] = useState(null);
  const [fileB64, setFileB64] = useState("");
  const [filePreview, setFilePreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File too large. Please upload a file under 20 MB.");
      return;
    }
    // PDFs: read as-is (can't compress)
    if (f.type === "application/pdf") {
      setFile(f);
      setFilePreview("");
      const reader = new FileReader();
      reader.onload = () => setFileB64(String(reader.result).split(",")[1] || "");
      reader.readAsDataURL(f);
      return;
    }
    // Images: compress to max 1600px wide, JPEG quality 0.82, to avoid CDN size limits
    compressImage(f).then(({ blob, dataUrl }) => {
      const compressedFile = new File([blob], f.name.replace(/\.(heic|heif|png|webp)$/i, ".jpg"), { type: "image/jpeg" });
      setFile(compressedFile);
      setFilePreview(dataUrl);
      setFileB64(dataUrl.split(",")[1] || "");
    }).catch(() => {
      // Fallback: send the original if compression fails
      setFile(f);
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result);
        setFileB64(dataUrl.split(",")[1] || "");
        if (f.type.startsWith("image/")) setFilePreview(dataUrl);
      };
      reader.readAsDataURL(f);
    });
  };

  const clearFile = (e) => {
    e?.stopPropagation();
    setFile(null);
    setFileB64("");
    setFilePreview("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/consultations", {
        ...form,
        file_base64: fileB64,
        file_name: file?.name || "",
        file_mime: file?.type || "",
      });
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="consultation-success" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white border-2 border-gold rounded-lg p-8 shadow-md">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-10 h-10 text-gold" />
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-black">Consultation Booked!</h1>
          </div>
          <p className="mt-4 text-xl text-gray-800">
            Thank you, <span className="font-semibold">{done.full_name}</span>. We've received your booking
            and a confirmation has been sent to <span className="font-semibold">{done.email}</span>.
          </p>
          <p className="mt-3 text-lg text-gray-700">
            Our team will get back to you shortly. If you need us sooner, call{" "}
            <a href={`tel:${BUSINESS.phoneRaw}`} className="font-bold text-black underline">{BUSINESS.phone}</a>.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href={`tel:${BUSINESS.phoneRaw}`} className="btn-primary"><Phone className="w-5 h-5" /> Call {BUSINESS.phone}</a>
            <Link to="/" className="btn-primary" data-testid="consult-back-home">Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="consultation-page" className="bg-slate-50 py-12 sm:py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold mb-4">
            <CalendarCheck className="w-7 h-7 text-black" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-black">Book a Free Consultation</h1>
          <p className="mt-4 text-xl text-gray-700">
            Quick and easy — just your name, phone, email, and a photo of your ticket. We'll get back to you shortly.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-10 bg-white border border-slate-200 rounded-lg p-6 sm:p-8 shadow-sm space-y-6">
          <Field label="Full Name *">
            <input data-testid="c-name" type="text" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="c-input" placeholder="Your full name" />
          </Field>
          <Field label="Phone Number *">
            <input data-testid="c-phone" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="c-input" placeholder="(587) 555-1234" />
          </Field>
          <Field label="Email Address *">
            <input data-testid="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="c-input" placeholder="you@example.com" />
          </Field>
          <Field label="Ticket Location">
            <input data-testid="c-location" type="text" value={form.ticket_location} onChange={(e) => setForm({ ...form, ticket_location: e.target.value })} className="c-input" placeholder="e.g. Calgary Glenmore" />
          </Field>

          <Field label="Upload a Photo of Your Ticket">
            <p className="text-base text-gray-600 -mt-1 mb-2">Optional — but it helps us prepare. (Phone photos are automatically resized for upload.)</p>
            {filePreview ? (
              <div data-testid="c-image-preview" className="relative border border-slate-300 rounded-lg overflow-hidden bg-white">
                <img src={filePreview} alt="Ticket preview" className="w-full max-h-96 object-contain bg-slate-50" />
                <div className="p-3 flex items-center justify-between border-t border-slate-200">
                  <div className="text-sm text-gray-700 truncate">
                    <span className="font-semibold">{file?.name}</span>
                    <span className="text-gray-500"> · {(file?.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="c-image-change" className="text-sm font-semibold text-black underline">Change</button>
                    <button type="button" onClick={clearFile} data-testid="c-image-remove" className="text-sm font-semibold text-red-600 underline">Remove</button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileRef.current?.click()}
                data-testid="c-dropzone"
                className="cursor-pointer border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-gold hover:bg-slate-50 transition-colors"
              >
                {file ? (
                  <div className="flex items-center justify-center gap-3 text-black">
                    <FileText className="w-7 h-7" />
                    <div className="text-left">
                      <div className="font-semibold text-lg">{file.name}</div>
                      <div className="text-sm text-gray-600">{(file.size / 1024).toFixed(0)} KB · click to change</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-gray-700">
                    <Upload className="w-8 h-8 text-gold" />
                    <div className="font-semibold text-lg">Click to upload</div>
                    <div className="text-sm">JPG, PNG, or PDF — up to 8 MB</div>
                  </div>
                )}
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={onFile}
              data-testid="c-file"
            />
          </Field>

          <Field label="Anything else we should know? (optional)">
            <textarea data-testid="c-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="c-input" placeholder="Brief details about your ticket or situation." />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            data-testid="c-submit"
            className="btn-primary w-full text-xl py-5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (<><Loader2 className="w-6 h-6 animate-spin" /> Booking...</>) : (<><CalendarCheck className="w-6 h-6" /> Book My Free Consultation</>)}
          </button>

          <p className="text-center text-base text-gray-600">
            Or call us now at <a href={`tel:${BUSINESS.phoneRaw}`} className="font-semibold text-black underline">{BUSINESS.phone}</a>
          </p>
        </form>
      </div>

      <style>{`.c-input{display:block;width:100%;height:56px;font-size:1.125rem;padding:0 1rem;border:1px solid #cbd5e1;border-radius:0.5rem;background:#fff;color:#111;}
      textarea.c-input{height:auto;padding:0.75rem 1rem;}
      .c-input:focus{outline:none;border-color:#0F172A;box-shadow:0 0 0 3px rgba(15,23,42,0.1);}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-lg font-semibold text-black mb-2 block">{label}</span>
      {children}
    </label>
  );
}

// Compress an image in the browser to max 1600px on the longest side, JPEG 0.82 quality.
// Avoids upstream CDN body-size limits and keeps phone photos under ~700 KB.
function compressImage(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(objectUrl);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("compress failed"));
            const reader = new FileReader();
            reader.onload = () => resolve({ blob, dataUrl: String(reader.result) });
            reader.onerror = () => reject(new Error("read failed"));
            reader.readAsDataURL(blob);
          },
          "image/jpeg",
          quality
        );
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("image load failed"));
    };
    img.src = objectUrl;
  });
}
